import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { Capture } from "../lib/api";
import { eventWhen, reminderWhen } from "../lib/derive";
import { relTime } from "../lib/format";
import { highlight, makeSnippet } from "../lib/highlight";
import { CATEGORY_META, Icon } from "../lib/icons";

interface Props {
  item: Capture;
  query: string;
  onDelete?: (item: Capture) => void;
}

const SWIPE_MAX = 88; // px — width of the revealed Delete action

export function Row({ item, query, onDelete }: Props) {
  const navigate = useNavigate();
  const meta = CATEGORY_META[item.category];
  const IconCmp = meta?.icon;
  const snippet = makeSnippet(item, query);
  const when =
    item.category === "reminder"
      ? reminderWhen(item)
      : item.category === "event"
        ? eventWhen(item)
        : null;
  const tags = item.tags ?? [];

  // Tax records are immutable: never expose swipe/trash delete in the feed.
  // Removal goes through the type-to-confirm modal on the detail page. The
  // flag also gates the swipe handlers below so the row can't be dragged open.
  const deletable = !!onDelete && item.category !== "tax";

  // Mobile swipe-to-delete: track a leftward drag and snap open/closed.
  const [dragX, setDragX] = useState(0);
  const startX = useRef<number | null>(null);

  const open = () => {
    if (dragX !== 0) {
      setDragX(0); // a swipe-open tap closes the action rather than navigating
      return;
    }
    navigate(`/c/${item.id}`);
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      open();
    }
  };

  const onTouchStart = (e: React.TouchEvent) => {
    if (!deletable) return;
    startX.current = e.touches[0].clientX - dragX;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!deletable || startX.current === null) return;
    const next = e.touches[0].clientX - startX.current;
    setDragX(Math.max(-SWIPE_MAX, Math.min(0, next)));
  };
  const onTouchEnd = () => {
    if (!deletable) return;
    startX.current = null;
    setDragX((x) => (x < -SWIPE_MAX / 2 ? -SWIPE_MAX : 0));
  };

  const del = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDragX(0);
    onDelete?.(item);
  };

  return (
    <li className="row-swipe" style={{ ["--cat-color" as string]: meta?.color }}>
      {deletable ? (
        <button
          className="row-delete"
          aria-hidden="true"
          tabIndex={-1}
          onClick={del}
          style={{ width: SWIPE_MAX }}
        >
          <Icon.trash />
          <span>Delete</span>
        </button>
      ) : null}
      <div
        className="row"
        tabIndex={0}
        role="link"
        onClick={open}
        onKeyDown={onKeyDown}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          transform: `translateX(${dragX}px)`,
          transition: startX.current === null ? undefined : "none",
        }}
      >
        <span className="row-icon">{IconCmp ? <IconCmp /> : null}</span>
        <div className="row-body">
          <div className="row-summary">{highlight(snippet, query)}</div>
          <div className="row-meta">
            {item.pinned ? (
              <span className="pin-dot" title="Pinned" aria-label="Pinned">
                📌
              </span>
            ) : null}
            <span
              style={{
                color: meta?.color,
                fontWeight: 500,
                letterSpacing: "var(--ls-chrome)",
              }}
            >
              {meta?.label ?? item.category}
            </span>
            {tags.length > 0 ? (
              <>
                <span className="sep" />
                <span className="row-tags">
                  {tags.slice(0, 3).map((t) => (
                    <button
                      key={t}
                      className="tag tag-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/?tag=${encodeURIComponent(t)}`);
                      }}
                    >
                      {t}
                    </button>
                  ))}
                </span>
              </>
            ) : null}
            {when ? (
              <>
                <span className="sep" />
                <span style={{ color: meta?.color }}>{when}</span>
              </>
            ) : null}
          </div>
        </div>
        <span className="row-time">{relTime(item.captured_at)}</span>
        {deletable ? (
          <button className="row-trash" aria-label="Delete" onClick={del}>
            <Icon.trash />
          </button>
        ) : null}
      </div>
    </li>
  );
}
